import React, { useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { useNavigate, useParams } from '@common/hooks';
import { AppContent, ErrorHandler, Tabs } from '@common/components';
import { SubmissionsTable } from './_submissions-table';
import { RequestsTable } from './_requests-table';
import { StaffingRequestsExportButton } from '@jobs/components';
import { StaffingRequestAssignmentsTable } from 'works/legacy/components/staffing-request-assignments-table';
import { useRequestsQueryHandler } from './_requests-query-handler';
import { useSubmissionsQueryHandler } from './_submissions-query-handler';

const URL_PARAMS = {
  requests: 'requests',
  submissions: 'submissions',
  offers: 'offers',
  extensions: 'extensions',
  accepted: 'accepted',
};

const localStorageFilterMap = {
  requests: 'StaffingRequestListTable',
  submissions: 'SuppliersubmissionsTable',
  offers: 'SupplierStaffingRequestsOffers',
  accepted: 'SupplierStaffingRequestsAccepted',
};

export function SupplierStaffingRequestsPage() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const currentTab =
    URL_PARAMS[tab as keyof typeof URL_PARAMS] || URL_PARAMS.requests;
  const [extensionOfferCount, setExtensionOfferCount] = useState(0);

  useSetBreadcrumbs([{ name: 'Staffing Requests', path: '/works/supplier' }]);

  // REQUESTS DATA
  const {
    requestsData,
    requestsError,
    requestsFetchMore,
    requestsLoading,
    requestsTotal,
  } = useRequestsQueryHandler({
    localStorageKey: localStorageFilterMap.requests,
  });

  // SUBMISSIONS DATA
  const {
    data: submissionsData,
    error: submissionsError,
    fetchMore: submissionsFetchMore,
    loading: submissionsLoading,
    total: submissionsTotal,
  } = useSubmissionsQueryHandler({
    localStorageKey: localStorageFilterMap.submissions,
    statesFilter: [
      'submitted',
      'submittal_packet_change_requested',
      'in_review',
      'rejected',
      'withdrawn',
    ],
  });

  // OFFERS DATA
  const {
    data: offersData,
    error: offersError,
    fetchMore: offersFetchMore,
    loading: offersLoading,
    total: offersTotal,
  } = useSubmissionsQueryHandler({
    localStorageKey: localStorageFilterMap.offers,
    statesFilter: ['offered'],
  });

  // ACCEPTED DATA
  const {
    data: acceptedData,
    error: acceptedError,
    fetchMore: acceptedFetchMore,
    loading: acceptedLoading,
    total: acceptedTotal,
  } = useSubmissionsQueryHandler({
    localStorageKey: localStorageFilterMap.accepted,
    statesFilter: ['accepted'],
  });

  const anyError =
    requestsError || submissionsError || offersError || acceptedError;

  if (anyError) {
    return <ErrorHandler error={anyError} log="StaffingRequestsPage" />;
  }

  return (
    <>
      <AppContent>
        <Tabs
          defaultActiveKey={currentTab}
          onChange={(key) => {
            navigate(`/works/supplier/staffing-requests/${key}`);
          }}
        >
          <Tabs.Pane
            className="staffing-requests-tabs"
            key={URL_PARAMS.requests}
            tab={<Tabs.Tab title="Requests" count={requestsTotal || 0} />}
          >
            {!requestsLoading && (
              <div style={{ position: 'relative' }}>
                <span className="staffing-request-export-button-wrapper">
                  <StaffingRequestsExportButton />
                </span>
              </div>
            )}
            <RequestsTable
              isLoaded={!requestsLoading}
              total={requestsTotal}
              data={requestsData}
              fetchMore={requestsFetchMore}
              localStorageFilterKey={localStorageFilterMap.requests}
            />
          </Tabs.Pane>
          <Tabs.Pane
            className="staffing-requests-tabs"
            key={URL_PARAMS.submissions}
            tab={<Tabs.Tab title="Submissions" count={submissionsTotal || 0} />}
          >
            <SubmissionsTable
              isLoaded={!submissionsLoading}
              total={submissionsTotal}
              data={submissionsData}
              localStorageFilterKey={localStorageFilterMap.submissions}
              fetchMore={submissionsFetchMore}
              statusFilters={[
                {
                  text: 'Submitted',
                  value: 'submitted',
                },
                {
                  text: 'Change Requested',
                  value: 'submittal_packet_change_requested',
                },
                {
                  text: 'In Review',
                  value: 'in_review',
                },
                {
                  text: 'Rejected',
                  value: 'rejected',
                },
                {
                  text: 'Withdrawn',
                  value: 'withdrawn',
                },
              ]}
            />
          </Tabs.Pane>
          <Tabs.Pane
            className="staffing-requests-tabs"
            key={URL_PARAMS.offers}
            tab={<Tabs.Tab title="Offers" count={offersTotal || 0} />}
          >
            <SubmissionsTable
              isOfferTable
              isLoaded={!offersLoading}
              total={offersTotal}
              data={offersData}
              fetchMore={offersFetchMore}
              localStorageFilterKey={localStorageFilterMap.offers}
              statusFilterKey="offerState"
              statusFilters={[
                {
                  text: 'Offer',
                  value: 'offered',
                },
                {
                  text: 'Offer Retracted',
                  value: 'retracted',
                },
                {
                  text: 'Offer Rejected',
                  value: 'rejected',
                },
                {
                  text: 'Offer Change Requested',
                  value: 'change_requested',
                },
              ]}
            />
          </Tabs.Pane>
          <Tabs.Pane
            className="staffing-requests-tabs"
            key={URL_PARAMS.accepted}
            tab={<Tabs.Tab title="Accepted" count={acceptedTotal || 0} />}
          >
            <SubmissionsTable
              isAcceptedTable
              isLoaded={!acceptedLoading}
              total={acceptedTotal}
              data={acceptedData}
              fetchMore={acceptedFetchMore}
              localStorageFilterKey={localStorageFilterMap.accepted}
              statusFilters={[
                {
                  text: 'Accepted',
                  value: 'accepted',
                },
              ]}
            />
          </Tabs.Pane>
          <Tabs.Pane
            forceRender={true}
            className="extensions"
            key={URL_PARAMS.extensions}
            tab={<Tabs.Tab title="Extensions" count={extensionOfferCount} />}
          >
            <StaffingRequestAssignmentsTable
              filtersOverride={{
                state: ['extension_sent', 'extension_change_requested'],
              }}
              returnCount={setExtensionOfferCount}
            />
          </Tabs.Pane>
        </Tabs>
      </AppContent>
      <style jsx>{`
        .staffing-request-export-button-wrapper {
          display: inline-block;
          position: absolute;
          right: 0px;
          top: -58px;
        }
      `}</style>
    </>
  );
}
